import { inject, Injectable } from '@angular/core';
import { IFeatureFlagService } from '@shared/services/feature-flags';
import { catchError, map, of, shareReplay, take } from 'rxjs';
import { toSignal } from '@angular/core/rxjs-interop';
import { FeatureFlagService } from '../../partage/feature-flag/feature-flag.service';

import type { Observable } from 'rxjs';
import type { FeatureFlag } from '@shared/services/feature-flags';

/**
 * Adaptateur du port `IFeatureFlagService` du kit vers le service BackStage.
 *
 * Le port du kit est **synchrone** (`isEnabled` renvoie un booleen), alors que le
 * service BackStage est asynchrone (il interroge l'API). On pre-charge donc la
 * table une fois et on repond depuis le cache ; tant qu'elle n'est pas arrivee,
 * la reponse est `false` — le kit n'active un drapeau que s'il est connu.
 *
 * **Fermeture par defaut, sur les DEUX methodes.** C'est la garantie du port
 * (`IFeatureFlagService`), et elle demande deux precautions ici :
 *
 * 1. `catchError` sur la source. Sans lui, une erreur HTTP est portee par le signal
 *    et RELANCEE a chaque lecture de `table()` (`toSignal` n'a plus d'option
 *    `rejectErrors` depuis Angular 20). Les deux consommateurs du port lisent dans
 *    un `effect` et un `computed` — la barre de navigation cesserait de s'afficher.
 * 2. Ne pas deleguer a `FeatureFlagService.isEnabled()`, qui rend `?? true` pour un
 *    drapeau ABSENT : c'est l'inverse de la garantie. Les deux methodes lisent
 *    desormais la meme table normalisee.
 */
@Injectable({ providedIn: 'root' })
export class FeatureFlagsBackstageAdapter extends IFeatureFlagService {
  private readonly source = inject(FeatureFlagService);

  private readonly table$ = this.source.getFeaturesFlags().pipe(
    catchError(() => of({} as Record<string, boolean>)),
    shareReplay(1),
  );

  private readonly table = toSignal(this.table$, { initialValue: {} as Record<string, boolean> });

  isEnabled(flag: FeatureFlag): boolean {
    return this.table()[flag] ?? false;
  }

  isEnabledWhenReady(flag: FeatureFlag): Observable<boolean> {
    // `take(1)` : le port demande de trancher UNE fois, a la premiere resolution.
    // `shareReplay` de la source rend la valeur immediatement si elle est deja la
    return this.table$.pipe(
      map(table => table[flag] ?? false),
      take(1),
    );
  }
}
